import { useCallback, useMemo, useRef, useState } from 'react';

import { t } from '@grafana/i18n';
import { Drawer } from '@grafana/ui';
import { QueryLibraryContext, useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { SavedQueriesDetailsPanel, type SavedQuerySaveAction } from '../SavedQueriesDetailsPanel';

type Props = {
  query: SavedQuery | undefined;
  onClose: () => void;
  /** Called with the deleted query's uid so the page can prune it from its bulk selection. */
  onDeleted?: (uid: string) => void;
  /** Called with the newly-created query's uid after saving a new/duplicated query. */
  onSaveNew?: (uid: string) => void;
  /**
   * When provided, the panel footer renders a split Save button (see SavedQueriesDetailsPanel).
   */
  saveActions?: SavedQuerySaveAction[];
  /**
   * Where the Explore editing banner should return to after Save / Discard. Supplied by the owning page,
   * which owns the `?editQuery` contract this drawer opens from (list: `?editQuery=<uid>`; detail:
   * `/saved-queries/:uid?editQuery=true`). Omit when the query has no uid yet.
   */
  savedQueryReturnTo?: string;
};

export function EditQueryDrawer({ query, onClose, onDeleted, onSaveNew, saveActions, savedQueryReturnTo }: Props) {
  const parent = useQueryLibraryContext();
  const [isEditing, setIsEditing] = useState(!!query);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const closeGuardRef = useRef<() => boolean>(() => true);

  const guardedClose = useCallback(
    (_isSelectingQuery?: boolean, _closedToEditInExplore?: boolean): boolean => {
      if (!closeGuardRef.current()) {
        // Guard opened the discard-changes confirmation modal; abort the close so any pending
        // navigation (Edit in Explore) can bail out too.
        return false;
      }
      onClose();
      return true;
    },
    [onClose]
  );

  // The drawer inherits the render context (and its analytics stamping) from the parent provider
  const value = useMemo(
    () => ({
      ...parent,
      closeDrawer: guardedClose,
      onSave: onClose,
      isEditingQuery: isEditing,
      setIsEditingQuery: setIsEditing,
      templateVariableOverrides: overrides,
      setTemplateVariableOverrides: setOverrides,
      setCloseGuard: (shouldAllowClose: () => boolean) => {
        closeGuardRef.current = shouldAllowClose;
      },
      clearCloseGuard: () => {
        closeGuardRef.current = () => true;
      },
      newQuery: undefined,
      openedToSaveQuery: false,
    }),
    [parent, isEditing, overrides, onClose, guardedClose]
  );

  if (!query) {
    return null;
  }

  const title = query.uid
    ? t('saved-queries.page.edit-drawer.title', 'Edit query')
    : t('saved-queries.page.edit-drawer.new-title', 'New query');

  return (
    <Drawer title={title} onClose={guardedClose}>
      <QueryLibraryContext.Provider value={value}>
        <SavedQueriesDetailsPanel
          query={query}
          onSaveNew={onSaveNew}
          saveActions={saveActions}
          standalone
          savedQueryReturnTo={savedQueryReturnTo}
          onDelete={() => {
            // Prune the deleted query from the page's bulk selection before closing, matching the
            // bulk/row-menu delete paths — otherwise the page stays in bulk mode with a stale uid.
            if (query.uid) {
              onDeleted?.(query.uid);
            }
            onClose();
          }}
        />
      </QueryLibraryContext.Provider>
    </Drawer>
  );
}
